import React, { useState, useEffect, type SyntheticEvent, useRef } from 'react';
import { styled } from '@compiled/react';
import { di } from 'react-magnetic-di';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import LinkIcon from '@atlaskit/icon/glyph/link';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import FieldCopyText from '@atlassian/jira-common-components-field-copy-text/src/view.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';

export type Props = {
	isVisible: boolean;
	id: string;
	label: string;
	fullIssueUrl: string;
	onCopyClick: (e: SyntheticEvent, event: UIAnalyticsEvent) => void;
};
export const PermalinkButton = ({ id, label, fullIssueUrl, isVisible, onCopyClick }: Props) => {
	di(clearTimeout);
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const [isVisibleWithLag, setIsVisibleWithLag] = useState<boolean>(false);
	const visibleWithLagTimerRef = useRef<ReturnType<typeof setTimeout>>();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

	const focusButtonAfterClick = () => {
		timeoutIdRef.current = setTimeout(() => buttonRef.current?.focus());
	};

	useEffect(() => () => clearTimeout(timeoutIdRef.current), []);

	useEffect(() => {
		if (isVisible) {
			if (visibleWithLagTimerRef.current) {
				clearTimeout(visibleWithLagTimerRef.current);
				visibleWithLagTimerRef.current = undefined;
			}
			setIsVisibleWithLag(isVisible);
		} else {
			visibleWithLagTimerRef.current = setTimeout(() => setIsVisibleWithLag(isVisible), 200);
		}
	}, [isVisible]);

	return (
		<CopyLinkButtonWrapper
			isVisible={isVisible && isVisibleWithLag}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="issue_view_permalink_button_wrapper"
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			data-testid="issue.common.component.permalink-button.button.copy-link-button-wrapper"
		>
			<FieldCopyText
				id={id}
				label={label}
				tooltipDelay={0}
				tooltipLabel={label}
				tooltipLabelPosition="top"
				shouldFitContainer
				value={fullIssueUrl}
				hideFieldText
				remountOnChange
				onCopyClick={onCopyClick}
				onClickChange={(status) => setIsCopied(status)}
				onCopyReset={() => setIsCopied(false)}
				onCopyFinish={focusButtonAfterClick}
			>
				<Button
					ref={buttonRef}
					appearance="subtle-link"
					iconBefore={
						isCopied ? (
							<CheckCircleIcon
								label=""
								primaryColor={token('color.icon.success', colors.G500)}
								size="small"
								/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
								testId="issue.common.component.permalink-button.button.check-circle-icon"
							/>
						) : (
							<LinkIcon
								size="small"
								label={label}
								/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
								testId="issue.common.component.permalink-button.button.link-icon"
							/>
						)
					}
					spacing="none"
					interactionName="auto-generated.jira-issue-view-common.component.permalink-button.button.button"
				/>
			</FieldCopyText>
		</CopyLinkButtonWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CopyLinkButtonWrapper = styled.div<{ isVisible: boolean }>(
	{
		paddingTop: token('space.050', '4px'),
		opacity: 0,
		transition: 'opacity 0.2s ease, transform 0.2s ease',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		transform: `translate(${-gridSize}px, 0)`,
		paddingLeft: token('space.050', '4px'),
		display: 'flex',
		alignItems: 'flex-end',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isVisible
			? {
					opacity: 1,
					transform: 'translate(0, 0)',
				}
			: undefined,
	{
		'&:focus': {
			opacity: 1,
			transform: 'translate(0, 0)',
		},
	},
);
